class HTMLUpdateUtility {
  /**
   * Used to swap an HTML node with a new node.
   * The new node is inserted as a previous sibling to the old node, the old node is hidden, and then the old node is removed.
   *
   * The function currently uses a double buffer approach, but this should be replaced by a view transition once it is more widely supported https://developer.mozilla.org/en-US/docs/Web/API/View_Transitions_API
   */
  static viewTransition(
    oldNode,
    newContent,
    preProcessCallbacks = [],
    postProcessCallbacks = []
  ) {
    preProcessCallbacks?.forEach((callback) => callback(newContent));

    const newNodeWrapper = document.createElement("div");
    HTMLUpdateUtility.setInnerHTML(newNodeWrapper, newContent.outerHTML);
    const newNode = newNodeWrapper.firstChild;

    // dedupe IDs
    const uniqueKey = Date.now();
    oldNode.querySelectorAll("[id], [form]").forEach((element) => {
      element.id && (element.id = `${element.id}-${uniqueKey}`);
      element.form &&
        element.setAttribute(
          "form",
          `${element.form.getAttribute("id")}-${uniqueKey}`
        );
    });

    oldNode.parentNode.insertBefore(newNode, oldNode);
    oldNode.style.display = "none";

    postProcessCallbacks?.forEach((callback) => callback(newNode));

    setTimeout(() => oldNode.remove(), 500);
  }

  // Sets inner HTML and reinjects the script tags to allow execution. By default, scripts are disabled when using element.innerHTML.
  static setInnerHTML(element, html) {
    element.innerHTML = html;
    element.querySelectorAll("script").forEach((oldScriptTag) => {
      const newScriptTag = document.createElement("script");
      Array.from(oldScriptTag.attributes).forEach((attribute) => {
        newScriptTag.setAttribute(attribute.name, attribute.value);
      });
      newScriptTag.appendChild(document.createTextNode(oldScriptTag.innerHTML));
      oldScriptTag.parentNode.replaceChild(newScriptTag, oldScriptTag);
    });
  }
}

function formatSelectedOptionValue(value) {
  const wrappedValueMatch = value.match(/^([^()]+)\(([^()]+)\)$/);
  return wrappedValueMatch && wrappedValueMatch[1].trim() && wrappedValueMatch[2].trim()
    ? wrappedValueMatch[2]
    : value;
}

class QuantityInputDawn extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector("input");
    this.changeEvent = new Event("change", { bubbles: true });
    this.input.addEventListener("change", this.onInputChange.bind(this));
    this.querySelectorAll("button").forEach((button) =>
      button.addEventListener("click", this.onButtonClick.bind(this))
    );
  }

  quantityUpdateUnsubscriber = undefined;

  connectedCallback() {
    this.validateQtyRules();
    this.quantityUpdateUnsubscriber = subscribe(
      PUB_SUB_EVENTS.quantityUpdate,
      this.validateQtyRules.bind(this)
    );
  }

  disconnectedCallback() {
    if (this.quantityUpdateUnsubscriber) {
      this.quantityUpdateUnsubscriber();
    }
    // Clear session storage when component is disconnected
    sessionStorage.removeItem('userVariantSelection');
  }

  onInputChange(event) {
    this.validateQtyRules();
  }

  onButtonClick(event) {
    event.preventDefault();
    const previousValue = this.input.value;

    if (event.target.name === "plus") {
      if (
        parseInt(this.input.dataset.min) > parseInt(this.input.step) &&
        this.input.value == 0
      ) {
        this.input.value = this.input.dataset.min;
      } else {
        this.input.stepUp();
      }
    } else {
      this.input.stepDown();
    }

    if (previousValue !== this.input.value)
      this.input.dispatchEvent(this.changeEvent);

    if (
      this.input.dataset.min === previousValue &&
      event.target.name === "minus"
    ) {
      this.input.value = parseInt(this.input.min);
    }
  }

  validateQtyRules() {
    const value = parseInt(this.input.value);
    if (this.input.min) {
      const buttonMinus = this.querySelector(".quantity__button[name='minus']");
      buttonMinus.classList.toggle(
        "disabled",
        parseInt(value) <= parseInt(this.input.min)
      );
    }
    if (this.input.max) {
      const max = parseInt(this.input.max);
      const buttonPlus = this.querySelector(".quantity__button[name='plus']");
      buttonPlus.classList.toggle("disabled", value >= max);
    }
  }
}

customElements.define("quantity-input-dawn", QuantityInputDawn);

class ProductVariantSelects extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.addEventListener("change", (event) => {
      const target = this.getInputForEventTarget(event.target);
      this.updateSelectionMetadata(event);

      // Store the current selection to prevent bouncing
      const currentSelection = {
        optionName: target.closest('.product-form__input').querySelector('input[type="radio"], select').name,
        value: target.value,
        timestamp: Date.now()
      };
      
      // Store in sessionStorage to persist across AJAX requests
      sessionStorage.setItem('userVariantSelection', JSON.stringify(currentSelection));

      // Ensure filtering runs after variant selection
      const productInfo = this.closest("product-info");
      if (productInfo && productInfo.productMediaGallery) {
        // Multiple timeouts to ensure it runs at different points in the update cycle
        setTimeout(() => productInfo.filterGalleryBySku(), 50);
        setTimeout(() => productInfo.filterGalleryBySku(), 150);
        setTimeout(() => productInfo.filterGalleryBySku(), 300);
      }

      // Add a safeguard URL update check after a brief delay to ensure URL is properly updated
      setTimeout(() => {
        if (this.currentVariant && productInfo) {
          const currentVariantInUrl = new URLSearchParams(window.location.search).get('variant');
          if (currentVariantInUrl !== this.currentVariant.id.toString()) {
            this.updateUrlImmediate(productInfo);
          }
        }
      }, 100);

      publish(PUB_SUB_EVENTS.optionValueSelectionChange, {
        data: {
          event,
          target,
          selectedOptionValues: this.selectedOptionValues,
        },
      });
    });
    
    // Restore user selection after DOM updates
    this.restoreUserSelection();
  }

  restoreUserSelection() {
    const savedSelection = sessionStorage.getItem('userVariantSelection');
    if (!savedSelection) return;
    
    try {
      const selection = JSON.parse(savedSelection);
      // Only restore if it's recent (within 10 seconds)
      if (Date.now() - selection.timestamp > 10000) return;
      
      // Find and restore the selection
      setTimeout(() => {
        const inputs = this.querySelectorAll('input[type="radio"], select option');
        inputs.forEach(input => {
          const inputName = input.tagName === 'OPTION' ? input.parentElement.name : input.name;
          if (inputName === selection.optionName && input.value === selection.value) {
            if (input.type === 'radio' && !input.checked) {
              input.checked = true;
              // Update the displayed selected value
              const selectedDisplay = input.closest('.product-form__input').querySelector('[data-selected-value]');
              if (selectedDisplay) {
                selectedDisplay.textContent = formatSelectedOptionValue(selection.value);
              }
            } else if (input.tagName === 'OPTION' && !input.selected) {
              input.selected = true;
              input.parentElement.value = selection.value;
            }
          }
        });
      }, 100);
    } catch (e) {
      // Ignore parsing errors
    }
  }

  updateSelectionMetadata({ target }) {
    const { value, tagName } = target;

    if (tagName === "SELECT" && target.selectedOptions.length) {
      Array.from(target.options)
        .find((option) => option.getAttribute("selected"))
        ?.removeAttribute("selected");

      // change previous target.selectedOptions with new value
      const newSelectedOption = Array.from(target.options).find(
        (option) => option.value === value
      );
      if (newSelectedOption) {
        newSelectedOption.setAttribute("selected", "selected");
      }

      const swatchValue = target.selectedOptions[0].dataset.optionSwatchValue;
      const selectedDropdownSwatchValue = target
        .closest(".product-form__input")
        .querySelector("[data-selected-value] > .product-option__swatch");
      if (!selectedDropdownSwatchValue) return;
      if (swatchValue) {
        selectedDropdownSwatchValue.style.setProperty(
          "--swatch--background",
          swatchValue
        );
        selectedDropdownSwatchValue.classList.remove(
          "product-option__swatch--unavailable"
        );
      } else {
        selectedDropdownSwatchValue.style.setProperty(
          "--swatch--background",
          "unset"
        );
        selectedDropdownSwatchValue.classList.add(
          "product-option__swatch--unavailable"
        );
      }

      selectedDropdownSwatchValue.style.setProperty(
        "--swatch-focal-point",
        target.selectedOptions[0].dataset.optionSwatchFocalPoint || "unset"
      );
    } else if (tagName === "INPUT" && target.type === "radio") {
      const selectedSwatchValue = target
        .closest(`.product-form__input`)
        .querySelector("[data-selected-value]");
      if (selectedSwatchValue) {
        selectedSwatchValue.textContent = formatSelectedOptionValue(value);
      }
    }
    
    // Check if this is a material/type change that affects other options
    this.handleMaterialColorDependency(target);
    
    // Immediately update the variant inputs to reflect the current selection
    this.updateVariantInputsImmediate();
    
    // Immediately update URL if we can determine the variant
    const productInfo = this.closest("product-info");
    if (productInfo) {
      this.updateUrlImmediate(productInfo);
    }
  }
  
  handleMaterialColorDependency(changedTarget) {
    // Get all variants to understand dependencies
    const allVariantsScript = document.querySelector('[data-all-variants]');
    if (!allVariantsScript) return;
    
    try {
      const allVariants = JSON.parse(allVariantsScript.textContent);
      const changedOptionContainer = changedTarget.closest('.product-form__input');
      const changedOptionName = changedOptionContainer.querySelector('legend, label').textContent.trim().replace(/\s+.*$/, ''); // Get just the option name
      
      // If this looks like a material/type change, auto-select appropriate color
      if (changedOptionName.toLowerCase().includes('material') || 
          changedOptionName.toLowerCase().includes('type')) {
        
        // Find variants that match this material selection
        const matchingVariants = allVariants.filter(variant => {
          return variant.option1 === changedTarget.value || 
                 variant.option2 === changedTarget.value || 
                 variant.option3 === changedTarget.value;
        });
        
        if (matchingVariants.length > 0) {
          // Find the first available variant for this material
          const availableVariant = matchingVariants.find(v => v.available) || matchingVariants[0];
          
          // Auto-select the color option for this variant
          this.autoSelectColorForVariant(availableVariant, changedTarget.value);
        }
      }
    } catch (e) {
      // Silently handle JSON parsing errors
    }
  }
  
  autoSelectColorForVariant(targetVariant, changedMaterialValue) {
    // Find which position the material is in and get the color from other positions
    const materialPosition = [targetVariant.option1, targetVariant.option2, targetVariant.option3]
      .findIndex(option => option === changedMaterialValue);
    
    // Get the color value (assuming it's in a different position than material)
    const colorValue = materialPosition === 0 ? targetVariant.option2 : 
                      materialPosition === 1 ? targetVariant.option1 : 
                      targetVariant.option1; // Fallback
    
    if (colorValue && colorValue !== changedMaterialValue) {
      // Find and select the color input
      const colorInputs = this.querySelectorAll('input[type="radio"], select option');
      colorInputs.forEach(input => {
        if (input.value === colorValue) {
          if (input.type === 'radio' && !input.checked) {
            input.checked = true;
            
            // Update display value
            const selectedDisplay = input.closest('.product-form__input').querySelector('[data-selected-value]');
            if (selectedDisplay) {
              selectedDisplay.textContent = formatSelectedOptionValue(colorValue);
            }
            
            // Trigger change event
            input.dispatchEvent(new Event('change', { bubbles: true }));
          } else if (input.tagName === 'OPTION' && !input.selected) {
            input.selected = true;
            input.parentElement.value = colorValue;
            
            // Trigger change event
            input.parentElement.dispatchEvent(new Event('change', { bubbles: true }));
          }
        }
      });
    }
  }

  updateVariantInputsImmediate() {
    const productInfo = this.closest("product-info");
    if (!productInfo) return;
    
    // Get current selections to find the matching variant
    const selectedValues = this.selectedOptionValues;
    
    // Try to find the variant that matches current selections
    const allVariantsScript = document.querySelector('[data-all-variants]');
    if (allVariantsScript) {
      try {
        const allVariants = JSON.parse(allVariantsScript.textContent);
        
        // Alternative approach: match by option values in order
        const currentOptions = [];
        const optionContainers = this.querySelectorAll('.product-form__input');
        optionContainers.forEach((container, index) => {
          const radioInput = container.querySelector('input[type="radio"]:checked');
          const selectInput = container.querySelector('select');
          
          if (radioInput) {
            currentOptions[index] = radioInput.value;
          } else if (selectInput) {
            currentOptions[index] = selectInput.value;
          }
        });
        
        // Find variant that matches the selected options
        const cleanCurrentOptions = currentOptions.filter(Boolean);
        
        const matchingVariant = allVariants.find(variant => {
          // Check if variant options match selected options
          const variantOptions = [variant.option1, variant.option2, variant.option3].filter(Boolean);
          
          if (variantOptions.length !== cleanCurrentOptions.length) return false;
          
          return cleanCurrentOptions.every((option, index) => 
            variantOptions[index] === option
          );
        });
        
        if (matchingVariant) {
          // Update the variant input immediately
          const variantInputs = productInfo.querySelectorAll('input[name="id"]');
          variantInputs.forEach(input => {
            input.value = matchingVariant.id;
          });
          
          // Store the matching variant for URL update
          this.currentVariant = matchingVariant;
        }
      } catch (e) {
        // Ignore JSON parsing errors
      }
    }
  }

  updateUrlImmediate(productInfo) {
    // Try to determine the variant from current selections if not already set
    if (!this.currentVariant) {
      this.updateVariantInputsImmediate();
    }
    
    if (this.currentVariant) {
      // Check if URL updates are enabled (similar to the existing updateURL method)
      if (productInfo.dataset.updateUrl === "false") return;
      
      const currentUrl = window.location.pathname;
      const newUrl = `${currentUrl}?variant=${this.currentVariant.id}`;
      
      // Only update if the URL actually changed
      if (window.location.href !== window.location.origin + newUrl) {
        window.history.replaceState({}, "", newUrl);
        
        // Also update share button if it exists
        const shareButton = productInfo.querySelector("share-button");
        if (shareButton) {
          shareButton.updateUrl(`${window.shopUrl || window.location.origin}${newUrl}`);
        }
      }
    }
  }

  getInputForEventTarget(target) {
    return target.tagName === "SELECT" ? target.selectedOptions[0] : target;
  }

  get selectedOptionValues() {
    return Array.from(
      this.querySelectorAll("select option[selected], fieldset input:checked")
    ).map(({ dataset }) => dataset.optionValueId);
  }
}
customElements.define("product-variant-selects", ProductVariantSelects);

if (!customElements.get("product-info")) {
  customElements.define(
    "product-info",
    class ProductInfo extends HTMLElement {
      quantityInput = undefined;
      quantityForm = undefined;
      onVariantChangeUnsubscriber = undefined;
      cartUpdateUnsubscriber = undefined;
      abortController = undefined;
      pendingRequestUrl = null;
      preProcessHtmlCallbacks = [];
      postProcessHtmlCallbacks = [];

      constructor() {
        super();

        this.quantityInput = this.querySelector(".quantity__input");
        this.productMediaGallery = this.querySelector(
          `[id^="MediaGallery-${this.dataset.section}"]`
        );
        this.productVariantSelects = this.querySelector(
          "product-variant-selects"
        );
        this.updateTimeout = null; // For debouncing variant updates
      }

      connectedCallback() {
        this.initializeProductSwapUtility();

        this.onVariantChangeUnsubscriber = subscribe(
          PUB_SUB_EVENTS.optionValueSelectionChange,
          this.handleOptionValueChange.bind(this)
        );

        this.initQuantityHandlers();
        this.dispatchEvent(
          new CustomEvent("product-info:loaded", { bubbles: true })
        );
        this.initUpdateMedia();

        // Add direct click handlers for variant options
        const variantContainers = this.querySelectorAll(".product-form__input");
        if (variantContainers.length > 0) {
          variantContainers.forEach((container) => {
            container.addEventListener("click", (e) => {
              if (
                e.target.matches('input[type="radio"], .product-option__swatch')
              ) {
                setTimeout(() => this.filterGalleryBySku(), 100);
              }
            });
          });
        }

        // Run filtering on initial load
        if (
          this.productMediaGallery &&
          this.productMediaGallery.dataset.hideOtherVariantsMedia === "true"
        ) {
          this.filterGalleryBySku();
        }
      }

      // Debug method for troubleshooting if needed in the future
      forceFilterDebug() {
        const selectedVariant = this.getSelectedVariant(this);
        const allMediaItems =
          this.productMediaGallery?.querySelectorAll("[data-media-id]") || [];
        this.filterGalleryBySku();
      }

      // Method for filtering gallery images based on SKU or color
      filterGalleryBySku() {
        const selectedVariant = this.getSelectedVariant(this);
        if (!selectedVariant) {
          return;
        }

        const variantSku = selectedVariant.sku || "";

        // Extract color from options if available
        let selectedColor = "";
        if (selectedVariant.options && selectedVariant.options.length > 0) {
          // Try to find color option
          selectedVariant.options.forEach((option) => {
            if (
              typeof option === "string" &&
              (option.toLowerCase().includes("color") ||
                option.toLowerCase().includes("colour"))
            ) {
              selectedColor = option;
            }
          });
        }

        // Also try to extract color from option selectors
        if (!selectedColor) {
          const colorSelectors = this.querySelectorAll(
            ".product-form__input:not(.variant-option-size) input:checked, .product-form__input:not(.variant-option-size) option:checked"
          );
          colorSelectors.forEach((selector) => {
            const value = selector.value || "";
            if (value && !value.includes("size") && !value.includes("Size")) {
              selectedColor = value;
            }
          });
        }

        // Get all media items
        const mediaItems =
          this.productMediaGallery.querySelectorAll(".swiper-slide[data-media-id]");
        const thumbnails = this.productMediaGallery.querySelectorAll(
          ".product__thumbnail-item, .product__media-dot-wrapper"
        );

        // Track if we've matched any items
        let matchedItems = false;

        // Show/hide based on SKU or color in alt text
        mediaItems.forEach((item) => {
          const img = item.querySelector("img");
          if (!img) return;

          const altText = img.alt || "";
          // Check if alt text contains SKU or color
          const matchesSku = variantSku && altText.includes(variantSku);
          const matchesColor =
            selectedColor &&
            altText.toLowerCase().includes(selectedColor.toLowerCase());

          const shouldShow = matchesSku || matchesColor;

          if (shouldShow) {
            matchedItems = true;
          }

          // Apply visibility using both display and classes for maximum compatibility
          item.style.display = shouldShow ? "" : "none";
          item.classList.toggle("hidden", !shouldShow);
        });

        // Also handle thumbnails directly
        thumbnails.forEach((thumbnail) => {
          const img = thumbnail.querySelector("img");
          if (!img) return;

          const altText = img.alt || "";
          const matchesSku = variantSku && altText.includes(variantSku);
          const matchesColor =
            selectedColor &&
            altText.toLowerCase().includes(selectedColor.toLowerCase());

          const shouldShow = matchesSku || matchesColor;

          if (shouldShow) {
            matchedItems = true;
          }

          thumbnail.style.display = shouldShow ? "" : "none";
          thumbnail.classList.toggle("hidden", !shouldShow);
        });

        // If no matches found, show all as fallback
        if (!matchedItems) {
          mediaItems.forEach((item) => {
            item.style.display = "";
            item.classList.remove("hidden");
          });

          thumbnails.forEach((thumbnail) => {
            thumbnail.style.display = "";
            thumbnail.classList.remove("hidden");
          });
        }

        // Update any slider components
        this.productMediaGallery
          .querySelectorAll("swiper-product-gallery")
          .forEach((swiperSlide) => {
            if (swiperSlide.instance) {
              swiperSlide.instance.update();
            }

            const featuredMedia = Array.from(
              swiperSlide.querySelectorAll(".swiper-slide[data-media-id]")
            ).find(
              (media) =>
                Number(media.dataset.mediaId) ===
                  selectedVariant.featured_media?.id &&
                !media.classList.contains("hidden")
            );
            const activeMedia =
              featuredMedia ||
              swiperSlide.querySelector(
                ".swiper-slide[data-media-id]:not(.hidden)"
              );
            if (activeMedia) {
              swiperSlide.setActiveMedia(Number(activeMedia.dataset.mediaId));
            }
          });
      }

      addPreProcessCallback(callback) {
        this.preProcessHtmlCallbacks.push(callback);
      }

      initQuantityHandlers() {
        if (!this.quantityInput) return;

        this.quantityForm = this.querySelector(".product-form__quantity");
        if (!this.quantityForm) return;

        this.setQuantityBoundries();
        if (!this.dataset.originalSection) {
          this.cartUpdateUnsubscriber = subscribe(
            PUB_SUB_EVENTS.cartUpdate,
            this.fetchQuantityRules.bind(this)
          );
        }
      }

      initUpdateMedia() {
        const variantIdFromUrl =
          parseInt(
            window.location.search
              .split("?")
              .filter((item) => item.includes("variant="))[0]
              ?.split("=")[1]
          ) || null;
        if (!variantIdFromUrl) {
          return;
        }
        const variant = this.getSelectedVariant(this);
        if (!variant || !variantIdFromUrl) {
          return;
        }
        if (variant?.id === variantIdFromUrl) {
          this.updateMedia(variant?.featured_media?.id);
        }
      }

      disconnectedCallback() {
        this.onVariantChangeUnsubscriber();
        this.cartUpdateUnsubscriber?.();
        // Clear any pending update timeout
        if (this.updateTimeout) {
          clearTimeout(this.updateTimeout);
        }
      }

      initializeProductSwapUtility() {
        this.preProcessHtmlCallbacks.push((html) =>
          html
            .querySelectorAll(".scroll-trigger")
            .forEach((element) =>
              element.classList.add("scroll-trigger--cancel")
            )
        );
        this.postProcessHtmlCallbacks.push((newNode) => {
          window?.Shopify?.PaymentButton?.init();
          window?.ProductModel?.loadShopifyXR();
        });
      }

      handleOptionValueChange({
        data: { event, target, selectedOptionValues },
      }) {
        if (!this.contains(event.target)) return;

        this.resetProductFormState();

        const productUrl =
          target.dataset.productUrl ||
          this.pendingRequestUrl ||
          this.dataset.url;
        this.pendingRequestUrl = productUrl;
        const shouldSwapProduct = this.dataset.url !== productUrl;
        const shouldFetchFullPage =
          this.dataset.updateUrl === "true" && shouldSwapProduct;

        // If we're staying on the same product, delay the request to allow multiple quick changes
        if (!shouldSwapProduct) {
          // Clear any existing timeout
          if (this.updateTimeout) {
            clearTimeout(this.updateTimeout);
          }
          
          // Delay the request to prevent bouncing during rapid selections
          // But reduce the delay to minimize interference with immediate URL updates
          this.updateTimeout = setTimeout(() => {
            this.renderProductInfo({
              requestUrl: this.buildRequestUrlWithParams(
                productUrl,
                selectedOptionValues,
                shouldFetchFullPage
              ),
              targetId: target.id,
              callback: this.handleUpdateProductInfo(productUrl),
            });
          }, 50); // Reduced delay to minimize URL update conflicts
        } else {
          // For product swaps, execute immediately
          this.renderProductInfo({
            requestUrl: this.buildRequestUrlWithParams(
              productUrl,
              selectedOptionValues,
              shouldFetchFullPage
            ),
            targetId: target.id,
            callback: this.handleSwapProduct(productUrl, shouldFetchFullPage),
          });
        }
      }

      resetProductFormState() {
        const productForm = this.productForm;
        productForm?.toggleSubmitButton(true);
        productForm?.handleErrorMessage();
      }

      handleSwapProduct(productUrl, updateFullPage) {
        return (html) => {
          this.productModal?.remove();

          const selector = updateFullPage
            ? "product-info[id^='MainProduct']"
            : "product-info";
          const variant = this.getSelectedVariant(html.querySelector(selector));
          this.updateURL(productUrl, variant?.id);

          if (updateFullPage) {
            document.querySelector("head title").innerHTML =
              html.querySelector("head title").innerHTML;

            HTMLUpdateUtility.viewTransition(
              document.querySelector("main"),
              html.querySelector("main"),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          } else {
            HTMLUpdateUtility.viewTransition(
              this,
              html.querySelector("product-info"),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          }
        };
      }

      renderProductInfo({ requestUrl, targetId, callback }) {
        this.abortController?.abort();
        this.abortController = new AbortController();

        fetch(requestUrl, { signal: this.abortController.signal })
          .then((response) => response.text())
          .then((responseText) => {
            this.pendingRequestUrl = null;
            const html = new DOMParser().parseFromString(
              responseText,
              "text/html"
            );
            callback(html);
          })
          .then(() => {
            // set focus to last clicked option value
            document.querySelector(`#${targetId}`)?.focus();
          })
          .catch((error) => {
            if (error.name === "AbortError") {
              // console.log("Fetch aborted by user");
            } else {
              // console.error(error);
            }
          });
      }

      getSelectedVariant(productInfoNode) {
        const selectedVariant = productInfoNode.querySelector(
          "product-variant-selects [data-selected-variant]"
        )?.innerHTML;
        return !!selectedVariant ? JSON.parse(selectedVariant) : null;
      }

      buildRequestUrlWithParams(
        url,
        optionValues,
        shouldFetchFullPage = false
      ) {
        const params = [];

        !shouldFetchFullPage && params.push(`section_id=${this.sectionId}`);

        if (optionValues.length) {
          params.push(`option_values=${optionValues.join(",")}`);
        }

        return `${url}?${params.join("&")}`;
      }

      updateOptionValues(html) {
        const productVariantSelects = html.querySelector(
          "product-variant-selects"
        );
        if (productVariantSelects) {
          HTMLUpdateUtility.viewTransition(
            this.variantSelectors,
            productVariantSelects,
            this.preProcessHtmlCallbacks,
            // Post-process callback to restore user selection
            [(newNode) => {
              if (newNode && newNode.restoreUserSelection) {
                newNode.restoreUserSelection();
              }
            }]
          );
        }
      }

      handleUpdateProductInfo(productUrl) {
        return async (html) => {
          const variant = this.getSelectedVariant(html);

          // this.pickupAvailability?.update(variant);
          this.updateOptionValues(html);
          
          // Only update URL if it hasn't been updated already by immediate update
          const currentVariantId = new URLSearchParams(window.location.search).get('variant');
          if (!currentVariantId || currentVariantId !== variant?.id?.toString()) {
            this.updateURL(productUrl, variant?.id);
          }
          
          this.updateVariantInputs(variant?.id);
          this.updateVariantDiscountBadge(
            variant?.price,
            variant?.compare_at_price
          );
          this.updateVariantDetails(variant?.id);

          // Always try to run the filter regardless of dataset setting
          if (this.productMediaGallery) {
            this.filterGalleryBySku();
            // Run again after a delay to ensure everything is updated
            setTimeout(() => this.filterGalleryBySku(), 200);
          }

          if (!variant) {
            this.setUnavailable();
            return;
          }

          // Fetch the preorder metafield
          let preorderStatus = false;
          try {
            const response = await fetch(
              `${productUrl}?variant=${variant.id}&section_id=${this.sectionId}`
            );
            const responseText = await response.text();
            const newHtml = new DOMParser().parseFromString(
              responseText,
              "text/html"
            );

            // Extract the preorder metafield if it exists
            const selectedPreorderElement = newHtml.querySelector(
              `input[data-variant-id="${variant.id}"]`
            );
            if (selectedPreorderElement) {
              preorderStatus =
                selectedPreorderElement.dataset.preorder === "true";
            }
          } catch (error) {
            console.error("Error fetching preorder metafield:", error);
          }

          this.updateMedia(variant?.featured_media?.id);

          const updateSourceFromDestination = (
            id,
            shouldHide = (source) => false
          ) => {
            const source = html.getElementById(`${id}-${this.sectionId}`);
            const destination = this.querySelector(
              `#${id}-${this.dataset.section}`
            );

            if (source && destination) {
              destination.innerHTML = source.innerHTML;

              if (id === "Inventory") {
                destination.dataset.inventoryQuantity =
                  source.dataset.inventoryQuantity;
                destination.dataset.inventoryThreshold =
                  source.dataset.inventoryThreshold;
                destination.classList.toggle(
                  "hidden",
                  source.classList.contains("hidden")
                );
              } else {
                destination.classList.toggle("hidden", shouldHide(source));
              }
            }
          };

          updateSourceFromDestination("ProductPurchaseInfo");
          updateSourceFromDestination("ProductStickyCart");

          updateSourceFromDestination("price");
          updateSourceFromDestination("Sku", ({ classList }) =>
            classList.contains("hidden")
          );

          updateSourceFromDestination(
            "Inventory",
            ({ innerText }) => innerText === ""
          );

          this.updateQuantityRules(this.sectionId, html);
          this.querySelector(
            `#Quantity-Rules-${this.dataset.section}`
          )?.classList.remove("hidden");
          this.productForm?.toggleSubmitButton(
            html
              .getElementById(`ProductSubmitButton-${this.sectionId}`)
              ?.hasAttribute("disabled") ?? true,
            preorderStatus
          );

          publish(PUB_SUB_EVENTS.variantChange, {
            data: {
              sectionId: this.sectionId,
              html,
              variant,
            },
          });
        };
      }

      updateVariantMedia(html) {
        // Get the selected variant - accommodate both with and without html parameter
        const selectedVariant = html
          ? this.getSelectedVariant(html)
          : this.getSelectedVariant(this);
        if (!selectedVariant) {
          this.productMediaGallery
            .querySelectorAll("[data-media-group]")
            .forEach((element) => element.classList.remove("hidden"));
          return;
        }

        // Implementation replaced by filterGalleryBySku
        this.filterGalleryBySku();
      }

      updateVariantOptionMedia() {
        this.productMediaGallery
          .querySelectorAll("[data-media-group]:not(.hidden)")
          .forEach((element) => {});
      }

      updateVariantInputs(variantId) {
        this.querySelectorAll(
          `#product-form-${this.dataset.section}, #product-form-installment-${this.dataset.section}`
        ).forEach((productForm) => {
          const input = productForm.querySelector('input[name="id"]');
          input.value = variantId ?? "";
          input.dispatchEvent(new Event("change", { bubbles: true }));
        });
      }

      updateURL(url, variantId) {
        this.querySelector("share-button")?.updateUrl(
          `${window.shopUrl}${url}${variantId ? `?variant=${variantId}` : ""}`
        );

        if (this.dataset.updateUrl === "false") return;
        window.history.replaceState(
          {},
          "",
          `${url}${variantId ? `?variant=${variantId}` : ""}`
        );
      }

      updateSellingPlanURL(sellingPlanId, selectedVariantId) {
        if (!selectedVariantId) return;
        if (!sellingPlanId) {
          window.history.replaceState(
            {},
            "",
            `${window.location.href.replace(/&selling_plan=\d+/, "")}`
          );
        } else {
          const params = new URLSearchParams(window.location.search);
          params[params.has("variant") ? "set" : "append"](
            "variant",
            selectedVariantId
          );
          if (sellingPlanId) {
            params[params.has("selling_plan") ? "set" : "append"](
              "selling_plan",
              sellingPlanId
            );
          } else {
            params.delete("selling_plan");
          }
          window.history.replaceState(
            {},
            "",
            `${this.dataset.url}?${params.toString()}`
          );
        }
      }

      updateVariantDetails(variantId) {
        const productVariantDetails = this.querySelectorAll(
          ".product__metafield_for_variant-block"
        );
        if (productVariantDetails) {
          productVariantDetails.forEach((productVariantDetail) => {
            const block_id = productVariantDetail.getAttribute("data-block-id");
            const metafieldDataElement = this.querySelector(
              `#product__metafield_for_variant-block--${block_id}`
            );
            if (!metafieldDataElement) {
              return;
            }
            try {
              let productVariantDetailsData = JSON.parse(
                metafieldDataElement?.textContent
              );
              productVariantDetail.innerHTML =
                productVariantDetailsData?.[variantId]?.replaceAll(
                  "\n",
                  "<br />"
                ) || "";
            } catch (error) {
              console.error("Error parsing JSON", error);
            }
          });
        }
      }

      updateVariantDiscountBadge(price, compare_at_price) {
        const discountBadge = this.querySelector(
          ".product-card__badge--discount"
        );
        if (!discountBadge) return;

        if (compare_at_price > price) {
          discountBadge.classList.remove("hidden");
          const percentageElement = discountBadge.querySelector(".percentage");
          // calculate discount percentage
          const discountPercentage = Math.round(
            ((compare_at_price - price) / compare_at_price) * 100
          );
          // update discount percentage
          percentageElement.textContent = discountPercentage;
        } else {
          discountBadge.classList.add("hidden");
        }
      }

      setUnavailable() {
        this.productForm?.toggleSubmitButton(
          true,
          window.variantStrings.unavailable
        );

        const selectors = ["price", "Inventory", "Sku", "Quantity-Rules"]
          .map((id) => `#${id}-${this.dataset.section}`)
          .join(", ");
        document
          .querySelectorAll(selectors)
          .forEach(({ classList }) => classList.add("hidden"));
      }

      updateMedia(variantFeaturedMediaId) {
        if (!variantFeaturedMediaId) return;

        if (
          this.querySelector(".main-product__media")?.hasAttribute(
            "data-desktop-grid"
          ) &&
          window.innerWidth >= 750
        ) {
          this.scrollToImage(variantFeaturedMediaId);
        }

        const productMedia = this.querySelector("swiper-product-gallery");
        productMedia?.setActiveMedia(variantFeaturedMediaId);

        // Also trigger our filtering after media is updated
        if (this.productMediaGallery) {
          setTimeout(() => this.filterGalleryBySku(), 150);
        }
      }

      scrollToImage(variantFeaturedMediaId) {
        this.querySelectorAll("[data-media-id]").forEach((img) => {
          if (img.getAttribute("data-media-id") == variantFeaturedMediaId) {
            img.scrollIntoView({
              block: "end",
              behavior: "smooth",
            });
          }
        });
      }

      setQuantityBoundries() {
        const data = {
          cartQuantity: this.quantityInput.dataset.cartQuantity
            ? parseInt(this.quantityInput.dataset.cartQuantity)
            : 0,
          min: this.quantityInput.dataset.min
            ? parseInt(this.quantityInput.dataset.min)
            : 1,
          max: this.quantityInput.dataset.max
            ? parseInt(this.quantityInput.dataset.max)
            : null,
          step: this.quantityInput.step ? parseInt(this.quantityInput.step) : 1,
        };

        let min = data.min;
        const max = data.max === null ? data.max : data.max - data.cartQuantity;
        if (max !== null) min = Math.min(min, max);
        if (data.cartQuantity >= data.min) min = Math.min(min, data.step);

        this.quantityInput.min = min;

        if (max) {
          this.quantityInput.max = max;
        } else {
          this.quantityInput.removeAttribute("max");
        }
        this.quantityInput.value = min;

        publish(PUB_SUB_EVENTS.quantityUpdate, undefined);
      }

      fetchQuantityRules() {
        const currentVariantId = this.productForm?.variantIdInput?.value;
        if (!currentVariantId) return;

        this.querySelector(
          ".quantity__rules-cart .loading__spinner"
        ).classList.remove("hidden");
        fetch(
          `${this.dataset.url}?variant=${currentVariantId}&section_id=${this.dataset.section}`
        )
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(
              responseText,
              "text/html"
            );
            this.updateQuantityRules(this.dataset.section, html);
          })
          .catch((e) => console.error(e))
          .finally(() =>
            this.querySelector(
              ".quantity__rules-cart .loading__spinner"
            ).classList.add("hidden")
          );
      }

      updateQuantityRules(sectionId, html) {
        if (!this.quantityInput) return;
        this.setQuantityBoundries();

        const quantityFormUpdated = html.getElementById(
          `Quantity-Form-${sectionId}`
        );
        const selectors = [
          ".quantity__input",
          ".quantity__rules",
          ".quantity__label",
        ];
        for (let selector of selectors) {
          const current = this.quantityForm.querySelector(selector);
          const updated = quantityFormUpdated.querySelector(selector);
          if (!current || !updated) continue;
          if (selector === ".quantity__input") {
            const attributes = [
              "data-cart-quantity",
              "data-min",
              "data-max",
              "step",
            ];
            for (let attribute of attributes) {
              const valueUpdated = updated.getAttribute(attribute);
              if (valueUpdated !== null) {
                current.setAttribute(attribute, valueUpdated);
              } else {
                current.removeAttribute(attribute);
              }
            }
          } else {
            current.innerHTML = updated.innerHTML;
          }
        }
      }

      get productForm() {
        return this.querySelector(`product-form`);
      }

      get productModal() {
        return document.querySelector(`#ProductModal-${this.dataset.section}`);
      }

      get pickupAvailability() {
        return this.querySelector(`pickup-availability`);
      }

      get variantSelectors() {
        return this.querySelector("product-variant-selects");
      }

      get relatedProducts() {
        const relatedProductsSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          "related-products"
        );
        return document.querySelector(
          `product-recommendations[data-section-id^="${relatedProductsSectionId}"]`
        );
      }

      get quickOrderList() {
        const quickOrderListSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          "quick_order_list"
        );
        return document.querySelector(
          `quick-order-list[data-id^="${quickOrderListSectionId}"]`
        );
      }

      get sectionId() {
        return this.dataset.originalSection || this.dataset.section;
      }
    }
  );
}

if (!customElements.get("product-form")) {
  customElements.define(
    "product-form",
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector("form");
        this.variantIdInput.disabled = false;
        this.form.addEventListener("submit", this.onSubmitHandler.bind(this));
        this.cart =
          document.querySelector("cart-notification") ||
          document.querySelector("cart-drawer");
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector("span");

        if (document.querySelector("cart-drawer"))
          this.submitButton.setAttribute("aria-haspopup", "dialog");

        this.hideErrors = this.dataset.hideErrors === "true";

        // Initialize all product variant selectors with data-metafields attribute
        this.variantSelectors = this.querySelectorAll(
          ".js-product-group-variants"
        );
        this.variantSelectors.forEach((variantSelector) => {
          variantSelector.addEventListener(
            "change",
            this.onVariantChange.bind(this)
          );
        });
      }

      // Check if the selected variant is hidden (custom.hide_variant = true)
      isHiddenVariant(variantId) {
        // Find the selected variant in the product's variants data
        const variantsJson = document.querySelector(
          "script[data-product-variants-json]"
        );
        if (!variantsJson) return false;

        try {
          const variants = JSON.parse(variantsJson.textContent);
          const selectedVariant = variants.find(
            (variant) => variant.id === parseInt(variantId)
          );

          // Check if the variant has the custom.hide_variant metafield set to true
          return (
            selectedVariant &&
            selectedVariant.metafields &&
            selectedVariant.metafields.custom &&
            selectedVariant.metafields.custom.hide_variant === true
          );
        } catch (e) {
          console.error("Error checking for hidden variant:", e);
          return false;
        }
      }

      onSubmitHandler(evt) {
        evt.preventDefault();

        // Get the selected variant ID
        const variantId = this.variantIdInput?.value;

        // Check if this is a hidden variant
        if (this.isHiddenVariant(variantId)) {
          // Prevent adding hidden variant to cart
          const errorMessage = "This variant is not available for purchase.";
          this.handleErrorMessage(errorMessage);
          return;
        }

        // Continue with existing code
        const isPreorder = false; // TODO adjust if preorder is needed
        if (this.submitButton.getAttribute("aria-disabled") === "true") return;

        this.handleErrorMessage();

        this.submitButton.setAttribute("aria-disabled", true);
        this.submitButton.classList.add("loading");
        this.querySelector(".loading__spinner").classList.remove("hidden");

        const config = fetchConfig("javascript");
        config.headers["X-Requested-With"] = "XMLHttpRequest";
        delete config.headers["Content-Type"];

        const formData = new FormData(this.form);
        if (this.cart) {
          formData.append(
            "sections",
            this.cart.getSectionsToRender().map((section) => section.section)
          );
          formData.append("sections_url", window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
        config.body = formData;

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: "product-form",
                productVariantId: formData.get("id"),
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);

              const soldOutMessage =
                this.submitButton.querySelector(".sold-out-message");
              if (!soldOutMessage) return;
              this.submitButton.setAttribute("aria-disabled", true);
              this.submitButtonText.classList.add("hidden");
              soldOutMessage.classList.remove("hidden");
              this.error = true;
              return;
            } else if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }

            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: "product-form",
                productVariantId: formData.get("id"),
                cartData: response,
              });
            this.error = false;

            const productData = {
              id: formData.get("id"),
              name: response.title,
              price: response.price / 100,
              quantity: parseInt(formData.get("quantity") || 1),
              vendor: response.vendor,
              variant: response.variant_title,
              category: response.product_type,
            };

            window.dispatchCartEvent("gtm:add_to_cart", productData);

            this.cart.renderContents(response);
          })
          .catch((e) => {
            console.error(e);
          })
          .finally(() => {
            this.submitButton.classList.remove("loading");
            if (this.cart && this.cart.classList.contains("is-empty"))
              this.cart.classList.remove("is-empty");
            if (!this.error) this.submitButton.removeAttribute("aria-disabled");
            this.querySelector(".loading__spinner").classList.add("hidden");
          });
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper ||
          this.querySelector(".product-form__error-message-wrapper");
        if (!this.errorMessageWrapper) return;
        this.errorMessage =
          this.errorMessage ||
          this.errorMessageWrapper.querySelector(
            ".product-form__error-message"
          );

        this.errorMessageWrapper.toggleAttribute("hidden", !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text, preorder = false) {
        if (disable) {
          this.submitButton.setAttribute("disabled", "disabled");
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute("disabled");

          if (preorder) {
            this.submitButtonText.textContent = window.variantStrings.preorder;
            this.togglePreorderMessage(preorder);
          } else {
            this.submitButtonText.textContent = this.hasAttribute(
              "data-has-selling-plan"
            )
              ? window.variantStrings.addSubscriptionToCart
              : window.variantStrings.addToCart;
          }
        }
      }

      togglePreorderMessage(preorder) {
        // Function to toggle the preorder message on the inventory notice block
        const inventoryNotice = document.querySelector(
          ".product__inventory-notice"
        );

        if (inventoryNotice && preorder) {
          inventoryNotice.innerHTML = `
          <svg width="1em" height="1em" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M12.0001 9.746V13.246M12.0001 15.746V15.736M11.1372 3.21921L2.63179 17.7406C2.24133 18.4072 2.7221 19.246 3.49467 19.246H20.5054C21.2781 19.246 21.7587 18.4072 21.3684 17.7406L12.863 3.21921C12.4767 2.55975 11.5235 2.55975 11.1372 3.21921ZM12.2501 15.746C12.2501 15.8841 12.1382 15.996 12.0001 15.996C11.862 15.996 11.7501 15.8841 11.7501 15.746C11.7501 15.608 11.862 15.496 12.0001 15.496C12.1382 15.496 12.2501 15.608 12.2501 15.746Z" 
                  stroke="#EF2D2D" stroke-width="2" stroke-linecap="round"/>
          </svg>
          <span>${window.variantStrings.preorderInfo}</span>`;
          inventoryNotice.classList.remove("hidden");
        }
      }

      get variantIdInput() {
        return this.form.querySelector("[name=id]");
      }
    }
  );
}
